import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {GroupInterface, GroupMemberInterface} from '../../../../interfaces/groups/group.interface';
import {GroupsService} from '../../../../services/groups/groups.service';
import {
  PushAudienceSendResponse,
  PushAudienceType,
  PushNotificationsService,
} from '../../../../services/push-notifications/push-notifications.service';
import {UsersService} from '../../../../services/users/users.service';

type PushDestinationType =
  | 'everyone'
  | 'brand'
  | 'user'
  | 'existingUser'
  | 'company'
  | 'team'
  | 'marketingGroup';
type StructuredAudienceDestinationType = Extract<
  PushAudienceType,
  'company' | 'team' | 'marketingGroup' | 'existingUser' | 'everyone'
>;

interface PushDestinationOption {
  value: PushDestinationType;
  label: string;
  icon: string;
}

interface ChannelDestination {
  name: string;
  topic: string;
}

interface SelectableDestination {
  id: string;
  name: string;
  email?: string;
  memberType?: GroupMemberInterface;
}

interface DestinationMember {
  id: string;
  name: string;
  email?: string;
  emailAddress?: string;
  userEmail?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  nickname?: string;
  user?: {
    email?: string;
    emailAddress?: string;
    userEmail?: string;
    username?: string;
  };
}

interface DeliveryIssue {
  userId?: string;
  user?: string;
  reason: string;
}

type DeliveryIssueResponseItem = string | {
  userId?: string;
  user?: string;
  reason?: string;
};

@Component({
    selector: 'app-superadmin-users-push',
    templateUrl: './superadmin-users-push.component.html',
    styleUrls: ['./superadmin-users-push.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminUsersPushComponent implements OnInit {
  public destinationTypes: PushDestinationOption[] = [
    {
      value: 'everyone',
      label: 'Everyone',
      icon: 'public',
    },
    {
      value: 'brand',
      label: 'Brand',
      icon: 'sell',
    },
    {
      value: 'user',
      label: 'User ID',
      icon: 'person',
    },
    {
      value: 'existingUser',
      label: 'Existing User',
      icon: 'manage_accounts',
    },
    {
      value: 'company',
      label: 'Company',
      icon: 'business',
    },
    {
      value: 'team',
      label: 'Team',
      icon: 'groups',
    },
    {
      value: 'marketingGroup',
      label: 'Marketing Group',
      icon: 'group_work',
    },
  ];
  public brandDestinations: ChannelDestination[] = [];
  public selectableDestinations: SelectableDestination[] = [];
  public marketingGroups: SelectableDestination[] = [];
  public destinationSearchControl = this.formBuilder.control('');
  public loadingDestinations = false;
  public destinationLoadError = '';
  public lastDeliverySummary: PushAudienceSendResponse | null = null;
  public readonly existingUserSearchMinLength = 2;
  private readonly existingUserSearchLimit = 100;
  private existingUserSearchRequestId = 0;

  public form = this.formBuilder.group({
    destinationType: ['everyone' as PushDestinationType, Validators.required],
    destinationValue: ['everyone'],
    userId: [''],
    title: ['', [Validators.required, Validators.maxLength(80)]],
    message: ['', [Validators.required, Validators.maxLength(240)]],
  });

  constructor(
    private formBuilder: FormBuilder,
    private usersService: UsersService,
    private pushNotificationsService: PushNotificationsService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private groupsService: GroupsService,
  ) {}

  ngOnInit(): void {
    this.loadBrandDestinations();
    this.form.get('destinationType')?.valueChanges.subscribe((value) => {
      this.handleDestinationTypeChanged(value as PushDestinationType);
    });
    this.destinationSearchControl.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(() => {
        if (this.selectedDestinationType === 'existingUser') {
          this.loadExistingUserDestinations();
        } else {
          this.loadSelectableDestinations();
        }
      });
    this.route.queryParams.subscribe((params) => {
      if (params.userId) {
        this.form.patchValue({
          userId: params.userId,
          destinationType: 'user',
          destinationValue: '',
        });
      }
    });
  }

  get selectedDestinationType(): PushDestinationType {
    return this.form.get('destinationType')?.value as PushDestinationType;
  }

  get selectedDestinationOption(): PushDestinationOption | undefined {
    return this.destinationTypes.find((option) => option.value === this.selectedDestinationType);
  }

  get selectedDestinationLabel(): string {
    const type = this.selectedDestinationType;
    if (type === 'everyone') {
      return 'Everyone';
    }
    if (type === 'user') {
      return this.form.get('userId')?.value || 'No user selected';
    }
    if (type === 'brand') {
      return this.brandDestinations.find((item) => item.topic === this.form.get('destinationValue')?.value)?.name || 'No brand selected';
    }
    return this.activeSelectableDestinations.find((item) => item.id === this.form.get('destinationValue')?.value)?.name || 'No destination selected';
  }

  get activeSelectableDestinations(): SelectableDestination[] {
    return this.selectedDestinationType === 'marketingGroup'
      ? this.marketingGroups
      : this.selectableDestinations;
  }

  get filteredSelectableDestinations(): SelectableDestination[] {
    return this.selectableDestinations;
  }

  get existingUserSearchReady(): boolean {
    return this.normalizeSearch(this.destinationSearchControl.value).length >= this.existingUserSearchMinLength;
  }

  get filteredBrandDestinations(): ChannelDestination[] {
    const search = this.normalizeSearch(this.destinationSearchControl.value);
    if (!search) {
      return this.brandDestinations;
    }
    return this.brandDestinations.filter((item) => this.normalizeSearch(item.name).includes(search));
  }

  get filteredMarketingGroups(): SelectableDestination[] {
    const search = this.normalizeSearch(this.destinationSearchControl.value);
    if (!search) {
      return this.marketingGroups;
    }
    return this.marketingGroups.filter((group) => {
      return this.normalizeSearch(`${group.name} ${this.getTypeLabel(group.memberType)}`).includes(search);
    });
  }

  get canSend(): boolean {
    if (this.form.invalid) {
      return false;
    }
    const type = this.selectedDestinationType;
    if (type === 'everyone') {
      return true;
    }
    if (type === 'user') {
      return !!this.form.get('userId')?.value;
    }
    return !!this.form.get('destinationValue')?.value;
  }

  get messageLength(): number {
    return `${this.form.get('message')?.value || ''}`.length;
  }

  get titleLength(): number {
    return `${this.form.get('title')?.value || ''}`.length;
  }

  get skippedDeliveryIssues(): DeliveryIssue[] {
    return this.getSkippedDeliveryIssues(this.lastDeliverySummary);
  }

  get failureDeliveryIssues(): DeliveryIssue[] {
    return this.getFailureDeliveryIssues(this.lastDeliverySummary);
  }

  get hasDeliveryIssues(): boolean {
    return this.hasSkippedDeliveries || this.hasFailedDeliveries;
  }

  get hasSkippedDeliveries(): boolean {
    return Number(this.lastDeliverySummary?.skippedCount || 0) > 0;
  }

  get hasFailedDeliveries(): boolean {
    return Number(this.lastDeliverySummary?.failureCount || 0) > 0;
  }

  getTypeLabel(type: GroupMemberInterface | undefined): string {
    return {
      [GroupMemberInterface.Team]: 'Team',
      [GroupMemberInterface.Company]: 'Company',
      [GroupMemberInterface.Brand]: 'Brand',
      [GroupMemberInterface.ExistingUser]: 'User',
      [GroupMemberInterface.EmailAddress]: 'Email',
    }[type || GroupMemberInterface.ExistingUser] || 'Group';
  }

  destinationTrackBy(_: number, item: SelectableDestination): string {
    return item.id;
  }

  brandTrackBy(_: number, item: ChannelDestination): string {
    return item.topic;
  }

  private async loadBrandDestinations(): Promise<void> {
    try {
      const brands = await this.usersService.getBrands();
      this.brandDestinations = [];
      brands.forEach((brand: any) => {
        if (brand.redirectUrl) {
          let name = brand.redirectUrl.split('.')[0];
          if (name === 'my') {
            name = 'wallit';
          }
          this.brandDestinations.push({ name: `${brand.name} - Brand`, topic: name });
        }
      });
      this.brandDestinations.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error loading destinations', error);
      this.destinationLoadError = 'Unable to load brand destinations.';
    }
  }

  private handleDestinationTypeChanged(type: PushDestinationType): void {
    const destinationValue = type === 'everyone' ? 'everyone' : '';
    this.form.patchValue({ destinationValue }, { emitEvent: false });
    this.destinationSearchControl.setValue('', { emitEvent: false });
    this.selectableDestinations = [];
    this.existingUserSearchRequestId += 1;
    this.destinationLoadError = '';
    this.lastDeliverySummary = null;

    if (type === 'marketingGroup') {
      this.loadMarketingGroups();
      return;
    }
    if (this.getMarketingMemberType(type)) {
      this.loadSelectableDestinations();
    }
  }

  private getMarketingMemberType(type: PushDestinationType): GroupMemberInterface | null {
    switch (type) {
      case 'team':
        return GroupMemberInterface.Team;
      case 'company':
        return GroupMemberInterface.Company;
      case 'existingUser':
        return GroupMemberInterface.ExistingUser;
      default:
        return null;
    }
  }

  private async loadMarketingGroups(): Promise<void> {
    this.loadingDestinations = true;
    this.destinationLoadError = '';
    try {
      const groups = this.normalizeMarketingGroups(await this.groupsService.getAll());
      this.marketingGroups = groups.map((group: GroupInterface) => ({
        id: group.id,
        name: group.name,
        memberType: group.memberType,
      })).sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error loading marketing groups', error);
      this.destinationLoadError = 'Unable to load marketing groups.';
    } finally {
      this.loadingDestinations = false;
    }
  }

  private async loadSelectableDestinations(): Promise<void> {
    const memberType = this.getMarketingMemberType(this.selectedDestinationType);
    if (!memberType) {
      return;
    }
    const selectedDestinationValue = this.form.get('destinationValue')?.value || '';

    this.loadingDestinations = true;
    this.destinationLoadError = '';
    try {
      if (memberType === GroupMemberInterface.ExistingUser) {
        await this.loadExistingUserDestinations();
        return;
      }

      const response = await this.groupsService.getItemsByType(memberType, {
        pagination: {
          page: 1,
          limit: 1000,
          search: this.destinationSearchControl.value || '',
          selectedMembers: selectedDestinationValue
            ? [selectedDestinationValue]
            : [],
        },
        filter: { filters: [] },
      });
      if (response.success === false) {
        throw new Error(response.error);
      }
      this.selectableDestinations = this.normalizeDestinationMembers(response).map((member) => ({
        id: member.id,
        name: this.formatDestinationName(memberType, member),
        email: this.getDestinationMemberEmail(member),
        memberType,
      })).sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error loading destination items', error);
      this.destinationLoadError = 'Unable to load destinations.';
    } finally {
      this.loadingDestinations = false;
    }
  }

  private async loadExistingUserDestinations(): Promise<void> {
    const search = this.normalizeSearch(this.destinationSearchControl.value);
    const requestId = ++this.existingUserSearchRequestId;
    if (search.length < this.existingUserSearchMinLength) {
      this.selectableDestinations = this.getSelectedDestinationFallback();
      this.loadingDestinations = false;
      return;
    }
    this.loadingDestinations = true;
    this.destinationLoadError = '';
    try {
      const users = this.normalizeUsersResponse(await this.usersService.queryUsers(
        search,
        0,
        this.existingUserSearchLimit,
      )).slice(0, this.existingUserSearchLimit);
      if (requestId !== this.existingUserSearchRequestId || this.selectedDestinationType !== 'existingUser') {
        return;
      }
      const selectedFallback = this.getSelectedDestinationFallback();
      const destinations = users.map((user: any) => this.mapUserDestination(user))
        .filter((user: SelectableDestination) => !!user.id)
        .sort((a: SelectableDestination, b: SelectableDestination) => a.name.localeCompare(b.name));
      this.selectableDestinations = [
        ...selectedFallback.filter((selected) => !destinations.some((destination) => destination.id === selected.id)),
        ...destinations,
      ];
    } catch (error) {
      if (requestId !== this.existingUserSearchRequestId) {
        return;
      }
      console.error('Error searching users', error);
      this.destinationLoadError = 'Unable to search users.';
    } finally {
      if (requestId === this.existingUserSearchRequestId) {
        this.loadingDestinations = false;
      }
    }
  }

  private getSelectedDestinationFallback(): SelectableDestination[] {
    const selectedDestinationValue = this.form.get('destinationValue')?.value || '';
    if (!selectedDestinationValue) {
      return [];
    }
    const selectedDestination = this.selectableDestinations.find((destination) => destination.id === selectedDestinationValue);
    return selectedDestination ? [selectedDestination] : [];
  }

  private mapUserDestination(user: any): SelectableDestination {
    const email = this.getDestinationMemberEmail(user);
    const name = this.getUserDisplayName(user);
    return {
      id: user.id || user.userId,
      name: email ? `${name} (${email})` : name,
      email,
      memberType: GroupMemberInterface.ExistingUser,
    };
  }

  async send(): Promise<void> {
    if (!this.canSend) {
      return;
    }
    const destinationValue = this.form.get('destinationValue')?.value || '';
    const userId = this.form.get('userId')?.value || '';
    const title = this.form.get('title')?.value || '';
    const message = this.form.get('message')?.value || '';
    try {
      this.lastDeliverySummary = null;
      const audienceType = this.getAudienceType(this.selectedDestinationType);
      if (audienceType) {
        const response = await this.sendAudienceMessage(
          audienceType,
          audienceType === 'everyone' ? '' : destinationValue,
          title,
          message,
        );
        this.lastDeliverySummary = this.normalizeAudienceSendResponse(response);
      } else if (this.selectedDestinationType === 'user') {
        await this.pushNotificationsService.sendMessageToUser(userId, title, message);
      } else {
        await this.pushNotificationsService.sendMessageToChannel(destinationValue, title, message);
      }
      this.snackBar.open(this.getSuccessMessage(), 'Close', { duration: 4000 });
    } catch (error) {
      console.error('Error sending push notification', error);
      this.snackBar.open('Error sending push notification', 'Close', { duration: 3000 });
    }
  }

  private getAudienceType(type: PushDestinationType): StructuredAudienceDestinationType | null {
    switch (type) {
      case 'company':
      case 'team':
      case 'marketingGroup':
      case 'existingUser':
      case 'everyone':
        return type;
      default:
        return null;
    }
  }

  private sendAudienceMessage(
    audienceType: StructuredAudienceDestinationType,
    id: string,
    title: string,
    message: string,
  ): Promise<PushAudienceSendResponse> {
    const audience = id
      ? { type: audienceType, ids: [id] }
      : { type: audienceType };
    return this.pushNotificationsService.sendAudienceMessage({
      audience,
      title,
      message,
    });
  }

  private normalizeAudienceSendResponse(response: any): PushAudienceSendResponse {
    const plainResponse = typeof response?.plain === 'function' ? response.plain() : response;
    const summary = plainResponse?.data?.recipientCount !== undefined
      ? plainResponse.data
      : plainResponse;
    const skipped = this.normalizeDeliveryIssueList(summary?.skipped);
    const failures = this.normalizeDeliveryIssueList(summary?.failures);

    return {
      recipientCount: Number(summary?.recipientCount || 0),
      sentCount: Number(summary?.sentCount || 0),
      skippedCount: Number(summary?.skippedCount || 0),
      skipped,
      skippedByUserId: summary?.skippedByUserId,
      failureCount: Number(summary?.failureCount || 0),
      failures,
    };
  }

  private normalizeDeliveryIssueList(value: any): any[] {
    const plainValue = typeof value?.plain === 'function' ? value.plain() : value;
    const list = Array.isArray(plainValue)
      ? plainValue
      : Array.isArray(plainValue?.data)
        ? plainValue.data
        : plainValue && typeof plainValue === 'object'
          ? Object.keys(plainValue).map((userId) => ({
            userId,
            ...plainValue[userId],
          }))
        : [];

    return list.map((item: any) => typeof item?.plain === 'function' ? item.plain() : item);
  }

  private normalizeDestinationMembers(response: any): DestinationMember[] {
    const members = response?.data?.members || response?.members || response?.data || [];
    return Array.isArray(members) ? members : [];
  }

  private normalizeMarketingGroups(response: any): GroupInterface[] {
    if (Array.isArray(response)) {
      return response;
    }
    const groups = response?.data || response?.groups || [];
    return Array.isArray(groups) ? groups : [];
  }

  private normalizeUsersResponse(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    const users = response?.data || response?.users || response?.items || [];
    return Array.isArray(users) ? users : [];
  }

  private formatDestinationName(memberType: GroupMemberInterface, member: DestinationMember): string {
    const email = this.getDestinationMemberEmail(member);
    if (memberType === GroupMemberInterface.ExistingUser && email) {
      return `${member.name} (${email})`;
    }
    return member.name;
  }

  private getDestinationMemberEmail(member: DestinationMember | any): string {
    return `${member?.email
      || member?.emailAddress
      || member?.userEmail
      || member?.username
      || member?.user?.email
      || member?.user?.emailAddress
      || member?.user?.userEmail
      || member?.user?.username
      || ''}`.trim();
  }

  private getUserDisplayName(user: any): string {
    const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    return user?.name || fullName || user?.nickname || this.getDestinationMemberEmail(user) || user?.id || user?.userId || 'User';
  }

  private normalizeSearch(value: string | null | undefined): string {
    return `${value || ''}`.trim().toLowerCase();
  }

  private getSkippedDeliveryIssues(summary: PushAudienceSendResponse | null): DeliveryIssue[] {
    if (!summary) {
      return [];
    }

    const skippedItems = this.normalizeDeliveryIssueList(summary.skipped) as DeliveryIssueResponseItem[];
    if (skippedItems.length === 0 && Number(summary.skippedCount || 0) > 0) {
      return [{
        reason: 'skipped',
      }];
    }

    return skippedItems.map((skipped: DeliveryIssueResponseItem) => {
      if (typeof skipped === 'string') {
        return {
          user: skipped,
          reason: 'skipped',
        };
      }

      return {
        userId: skipped.userId,
        user: skipped.user,
        reason: skipped.reason || 'skipped',
      };
    });
  }

  private getFailureDeliveryIssues(summary: PushAudienceSendResponse | null): DeliveryIssue[] {
    if (!summary) {
      return [];
    }

    if (!summary.failures?.length && Number(summary.failureCount || 0) > 0) {
      return [{
        reason: 'failed',
      }];
    }

    if (!summary.failures?.length) {
      return [];
    }

    return summary.failures.map((failure) => ({
      userId: failure.userId,
      user: failure.user,
      reason: failure.error || 'failed',
    }));
  }

  getDeliveryIssueUser(issue: DeliveryIssue): string {
    return issue.user || issue.userId || 'Unknown user';
  }

  getDeliveryIssueReason(issue: DeliveryIssue): string {
    return `${issue.reason || ''}`
      .replace(/_/g, ' ')
      .trim() || 'No detail provided';
  }

  private getSuccessMessage(): string {
    if (!this.lastDeliverySummary) {
      return 'Push notification sent';
    }
    const summary = this.lastDeliverySummary;
    return `Push sent to ${summary.sentCount} of ${summary.recipientCount}; ${summary.skippedCount} skipped, ${summary.failureCount} failed`;
  }
}
